import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { useContext, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { getSizedImage } from 'utils/images';
import { BotContext } from '../../Bot.js';
import styles from './ChatWidget.module.css';
export const ChatWidget = ({ onToggle }) => {
    var _a;
    const intl = useIntl();
    const { useTailwindPrefix, position, options, notification } = useContext(BotContext);
    const [visible, setVisible] = useState(false);
    const [isHoveringAvatar, setIsHoveringAvatar] = useState(false);
    const [animationFinished, setAnimationFinished] = useState(false);
    useEffect(() => {
        if (visible && !animationFinished) {
            const timer = setTimeout(() => {
                setAnimationFinished(true);
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [visible, animationFinished]);
    useEffect(() => {
        var _a;
        if (!(notification === null || notification === void 0 ? void 0 : notification.enabled))
            return;
        if (notification.trigger === 'IMMEDIATE') {
            setVisible(true);
            return;
        }
        const timer = setTimeout(() => {
            setVisible(true);
        }, ((_a = notification.delaySeconds) !== null && _a !== void 0 ? _a : 0) * 1000);
        return () => clearTimeout(timer);
    }, [notification === null || notification === void 0 ? void 0 : notification.enabled, notification === null || notification === void 0 ? void 0 : notification.trigger, notification === null || notification === void 0 ? void 0 : notification.delaySeconds]);
    const handleClose = () => {
        setAnimationFinished(false);
        setVisible(false);
        setIsHoveringAvatar(false);
    };
    return (_jsxs("div", Object.assign({ className: classNames(Object.assign({}, (useTailwindPrefix
            ? {
                'tw-flex tw-flex-col tw-gap-2': true,
                'tw-items-end': position === 'bottom-right',
                'tw-items-start': position === 'bottom-left',
            }
            : {
                'flex flex-col gap-2': true,
                'items-end': position === 'bottom-right',
                'items-start': position === 'bottom-left',
            }))) }, { children: [(notification === null || notification === void 0 ? void 0 : notification.enabled) && (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ id: "chatbot-outside-close", className: classNames(Object.assign({}, (useTailwindPrefix
                            ? {
                                'tw-hidden': !(visible && animationFinished),
                                'tw-block': visible && animationFinished,
                                'tw-flex tw-w-full tw-max-w-[85%] tw-justify-end': true,
                            }
                            : {
                                hidden: !(visible && animationFinished),
                                block: visible && animationFinished,
                                'flex w-full max-w-[85%] justify-end': true,
                            }))) }, { children: _jsx("div", Object.assign({ className: classNames({
                                'mt-1 w-6 h-6 rounded-full flex items-center justify-center text-gray-500 bg-white/20 hover:text-black hover:bg-black/10 transition cursor-pointer': !useTailwindPrefix,
                                'tw-mt-1 tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-bg-white/20 hover:tw-text-black hover:tw-bg-black/10 tw-transition tw-cursor-pointer': useTailwindPrefix,
                            }), onClick: () => handleClose() }, { children: "\u00D7" })) })), _jsx("div", Object.assign({ id: "chatbot-outside-bubble", className: classNames(styles.slideUpAnimation, Object.assign({}, (useTailwindPrefix
                            ? {
                                'tw-hidden': !(visible || isHoveringAvatar),
                                'tw-block': visible || isHoveringAvatar,
                                'tw-z-10 tw-px-4 tw-py-3 tw-text-[15px] tw-rounded-[10px] tw-text-bot-accent-a11y tw-bg-bot-accent': true,
                                'tw-cursor-pointer': visible && animationFinished,
                                'tw-rounded-bl-none': position === 'bottom-left',
                                'tw-rounded-br-none': position === 'bottom-right',
                            }
                            : {
                                hidden: !(visible || isHoveringAvatar),
                                block: visible || isHoveringAvatar,
                                'z-10 px-4 py-3 text-[15px] rounded-[10px]  text-bot-accent-a11y bg-bot-accent': true,
                                'cursor-pointer': visible && animationFinished,
                                'rounded-br-none': position === 'bottom-right',
                                'rounded-bl-none': position === 'bottom-left',
                            }))) }, (visible && animationFinished && { onClick: () => onToggle() }), { children: intl.formatMessage({ id: `maia_chatbot_alertMessage` }, Object.assign({ name: (options === null || options === void 0 ? void 0 : options.name) || 'MaIA' }, ((options === null || options === void 0 ? void 0 : options.accountName) && { accountName: options.accountName }))) }))] })), _jsx("div", Object.assign({ id: "chatbot-outside-avatar", className: classNames(styles.avatarWithPulse, {
                    'z-20 bg-white rounded-full shadow-lg p-1 cursor-pointer border border-gray-200 transition hover:border-bot-accent': !useTailwindPrefix,
                    'tw-z-20 tw-bg-white tw-rounded-full tw-shadow-lg tw-p-1 tw-cursor-pointer tw-border tw-border-gray-200 tw-transition hover:tw-border-bot-accent': useTailwindPrefix,
                }), onMouseEnter: () => setIsHoveringAvatar(true), onMouseLeave: () => (!visible ? handleClose() : null), onClick: () => onToggle() }, { children: (options === null || options === void 0 ? void 0 : options.avatar) ? (_jsx("img", { src: getSizedImage(options.avatar.base + options.avatar.path, {
                        h: 56,
                        silhouette: 'CIRCLE',
                        fit: 'thumb',
                    }), srcSet: `
                  ${getSizedImage(options.avatar.base + options.avatar.path, {
                        h: 56,
                        silhouette: 'CIRCLE',
                        fit: 'thumb',
                        dpr: '2.0',
                    })} 2x
               `, alt: options.name, className: classNames({
                        'block w-14 h-14 rounded-[inherit] bg-white/20': !useTailwindPrefix,
                        'tw-block tw-w-14 tw-h-14 tw-rounded-[inherit] tw-bg-white/20': useTailwindPrefix,
                    }) })) : (_jsx("div", Object.assign({ className: classNames({
                        'w-14 h-14 rounded-[inherit] flex items-center justify-center bg-bot-accent text-bot-accent-a11y': !useTailwindPrefix,
                        'tw-w-14 tw-h-14 tw-rounded-[inherit] tw-flex tw-items-center tw-justify-center tw-bg-bot-accent tw-text-bot-accent-a11y': useTailwindPrefix,
                    }) }, { children: ((_a = options === null || options === void 0 ? void 0 : options.name) === null || _a === void 0 ? void 0 : _a.charAt(0).toUpperCase()) || 'M' }))) }))] })));
};
